import type {
  StudyArm,
  ConcentrationUnit,
  ValidationWarning,
  WarningCode,
  WarningSeverity,
} from '@bioavail/shared-types';
import {
  ConcentrationUnit as CU,
  WarningSeverity as WS,
  WarningCode as WC,
  TimeUnit,
  DoseUnit,
} from '@bioavail/shared-types';

/** Concentration unit class — mass/volume vs molar. */
function classifyConcentrationUnit(unit: ConcentrationUnit): 'mass' | 'molar' {
  switch (unit) {
    case CU.NgPerMl:
    case CU.UgPerMl:
    case CU.UgPerL:
    case CU.MgPerL:
      return 'mass';
    case CU.NmolPerL:
      return 'molar';
  }
}

/**
 * Normalize a concentration value to canonical ng/mL (PSD §4.2.8).
 */
export function normalizeConcentration(value: number, unit: ConcentrationUnit): number {
  switch (unit) {
    case CU.NgPerMl:
      return value;
    case CU.UgPerMl:
      return value * 1000; // µg/mL → ng/mL
    case CU.UgPerL:
      return value; // µg/L ≡ ng/mL
    case CU.MgPerL:
      return value * 1000; // mg/L → ng/mL
    case CU.NmolPerL:
      // Molar — cannot convert without molecular weight; pass through (blocked upstream).
      return value;
  }
}

/** Normalize a time value to canonical hours. */
export function normalizeTime(value: number, unit: TimeUnit): number {
  switch (unit) {
    case TimeUnit.Hour:
      return value;
    case TimeUnit.Minute:
      return value / 60;
    case TimeUnit.Day:
      return value * 24;
  }
}

/** Normalize a dose value to canonical mg. */
export function normalizeDose(value: number, unit: DoseUnit): number {
  switch (unit) {
    case DoseUnit.Mg:
      return value;
    case DoseUnit.Ug:
      return value / 1000;
    case DoseUnit.G:
      return value * 1000;
  }
}

/**
 * Cross-arm concentration-unit compatibility check (F-032).
 * Mass/volume (ng/mL, µg/mL, mg/L) vs molar (nmol/L) are incompatible without
 * molecular weight and are blocked. µg/mL ≡ mg/L are normalized silently.
 */
export function checkUnitCompatibility(arms: StudyArm[]): ValidationWarning[] {
  const warnings: ValidationWarning[] = [];
  const classes = new Set(arms.map((a) => classifyConcentrationUnit(a.concentrationUnit)));

  if (classes.size > 1) {
    warnings.push({
      code: WC.UnitMismatch,
      severity: WS.Block,
      message:
        'Cross-arm concentration units mix mass/volume and molar bases. Convert both arms to the same basis.',
      remediation:
        'Switch both arms to a mass/volume unit (ng/mL, µg/mL, mg/L) or provide a molecular weight for conversion.',
    });
  }

  return warnings;
}

/** Helper to construct a warning. */
export function warn(
  code: WarningCode,
  severity: WarningSeverity,
  message: string,
  remediation: string,
  armId?: string,
): ValidationWarning {
  return { code, severity, message, remediation, ...(armId ? { armId } : {}) };
}

export { WS, WC };
